// src/Components/VendorForm.js
import React, { useEffect, useReducer, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

/* -------------------------
   Static config & options
   ------------------------- */
const steps = [
  { id: 1, name: 'Basic Info', fields: ['vendorName', 'countries', 'uaeCities', 'uaeOffices'] },
  { id: 2, name: 'Operations', fields: ['vendorType', 'expertise', 'dataCollectionMethods', 'catiStations'] },
  { id: 3, name: 'Contacts', fields: ['contacts'] },
  { id: 4, name: 'Documents & Submit', fields: ['urgentRequests'] },
];

const OTHER_SPECIFY_OPTION = 'Others, specify';
const OTHER_SPECIFY_UAE_OFFICE = 'Others specify';

const vendorTypeOptions = [
  'Fieldwork',
  'Translation',
  'Automation and dashboards',
  'Coding',
  'Validation',
  OTHER_SPECIFY_OPTION,
];
const dataCollectionOptions = [
  'Face to face',
  'CATI',
  'MS',
  'Online Panel',
  'Online (Database)',
  'CLT',
  'Focus Group Discussions',
  'In-Depth Interviews',
  OTHER_SPECIFY_OPTION,
];
const countryOptions = ['UAE', 'KSA', 'Kuwait', 'Oman', 'Qatar', 'Bahrain', 'Iraq', 'Lebanon', 'Jordan', 'Egypt', 'Morocco', 'Others'];
const uaeCityOptions = ['Dubai', 'Abu Dhabi City', 'Al Ain', 'Al Dhafra', 'Sharjah', 'Northern Emirates'];
const uaeOfficeOptions = ['Dubai', 'Sharjah', 'Abu Dhabi', OTHER_SPECIFY_UAE_OFFICE];

/* -------------------------
   Helpers
   ------------------------- */
const OTHER_PREFIX = 'Others: ';

const formatArrayForUI = (arr) => (Array.isArray(arr) ? arr : []);
const joinOrDash = (arr) => (arr && arr.length ? arr.join(', ') : '—');

const parseOtherSpecifyField = (arr, otherOption) => {
  // Input: ['Fieldwork', 'Others: Something']
  // Output: { selected: ['Fieldwork', 'Others, specify'], otherValue: 'Something' }
  const selected = [];
  let otherValue = '';
  if (!Array.isArray(arr)) return { selected: [], otherValue: '' };

  arr.forEach((it) => {
    if (typeof it === 'string' && it.startsWith(OTHER_PREFIX)) {
      selected.push(otherOption);
      const candidate = it.substring(OTHER_PREFIX.length).trim();
      otherValue = candidate === 'Not specified' ? '' : candidate;
    } else if (typeof it === 'string') {
      selected.push(it);
    }
  });

  return { selected, otherValue };
};

const processOtherSpecifyField = (selectedArray, otherOption, otherValue) => {
  // Convert back to API representation
  const result = selectedArray.filter((s) => s !== otherOption);
  if (selectedArray.includes(otherOption)) {
    result.push(`${OTHER_PREFIX}${otherValue || 'Not specified'}`);
  }
  return result;
};

const buildFormDataPayload = (state) => {
  const formData = new FormData();
  formData.append('name', state.vendorName);
  formData.append('countries', JSON.stringify(state.countries));
  formData.append('uae_cities', JSON.stringify(state.countries.includes('UAE') ? state.uaeCities : []));
  const processedUaeOffices = processOtherSpecifyField(state.uaeOffices, OTHER_SPECIFY_UAE_OFFICE, state.otherUaeOffice);
  formData.append('uae_offices', JSON.stringify(state.countries.includes('UAE') ? processedUaeOffices : []));
  const processedVendorType = processOtherSpecifyField(state.vendorType, OTHER_SPECIFY_OPTION, state.otherVendorType);
  formData.append('vendor_type', JSON.stringify(processedVendorType));
  const processedDataCollection = processOtherSpecifyField(state.dataCollectionMethods, OTHER_SPECIFY_OPTION, state.otherDataCollectionMethod);
  formData.append('data_collection_methods', JSON.stringify(processedDataCollection));
  formData.append('expertise', state.expertise);
  if (state.dataCollectionMethods.includes('CATI')) {
    formData.append('cati_stations', String(state.catiStations || '0'));
  }
  formData.append('fieldworkers_count', String(state.fieldTeam.fieldworkers || '0'));
  formData.append('supervisors_count', String(state.fieldTeam.supervisors || '0'));
  formData.append('managers_count', String(state.fieldTeam.managers || '0'));
  formData.append('gov_permits_count', String(state.govPermits || '0'));
  formData.append('can_manage_urgent_requests', state.urgentRequests === 'yes');

  if (state.tradeLicense instanceof File) formData.append('trade_license', state.tradeLicense);
  if (state.credentials instanceof File) formData.append('credentials_document', state.credentials);

  const processedContacts = state.contacts.map((c, idx) => ({ id: c.id || null, name: c.name, email: c.email, mobile: c.mobile, is_primary: idx === 0 }));
  formData.append('contacts_data', JSON.stringify(processedContacts));

  return formData;
};

/* -------------------------
   Reducer & initial state
   ------------------------- */
const initialState = {
  vendorName: '',
  countries: [],
  uaeCities: [],
  uaeOffices: [],
  vendorType: [],
  expertise: '',
  dataCollectionMethods: [],
  catiStations: '',
  contacts: [{ id: null, name: '', email: '', mobile: '' }],
  fieldTeam: { fieldworkers: '', supervisors: '', managers: '' },
  govPermits: '',
  urgentRequests: 'no',
  tradeLicense: null,
  credentials: null,
  existingTradeLicenseUrl: '',
  existingCredentialsUrl: '',
  otherVendorType: '',
  otherDataCollectionMethod: '',
  otherUaeOffice: '',
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET':
      return { ...state, [action.key]: action.value };
    case 'SET_CONTACT':
      {
        const contacts = state.contacts.map((c, i) => (i === action.index ? { ...c, [action.field]: action.value } : c));
        return { ...state, contacts };
      }
    case 'ADD_CONTACT':
      return { ...state, contacts: [...state.contacts, { id: null, name: '', email: '', mobile: '' }] };
    case 'REMOVE_CONTACT':
      return { ...state, contacts: state.contacts.filter((_, i) => i !== action.index) };
    case 'SET_STATE':
      return { ...state, ...action.payload };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
}

/* -------------------------
   Small reusable UI components
   ------------------------- */
const Label = ({ htmlFor, children, required }) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700">
    {children} {required && <span className="text-red-500">*</span>}
  </label>
);

const MultiSelect = ({ id, options = [], value = [], onChange, helper }) => {
  return (
    <div>
      <select
        id={id}
        multiple
        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 bg-white"
        value={value}
        onChange={(e) => onChange(Array.from(e.target.selectedOptions, (o) => o.value))}
        aria-multiselectable="true"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      {helper && <p className="mt-1 text-xs text-gray-500">{helper}</p>}
    </div>
  );
};

const FileInput = ({ id, onFile, existingUrl }) => {
  return (
    <div>
      <input
        id={id}
        type="file"
        accept=".pdf,.doc,.docx,image/*"
        onChange={(e) => onFile(e.target.files?.[0] || null)}
        className="mt-1 block w-full text-sm"
      />
      {existingUrl && (
        <p className="text-xs mt-1">
          Current: <a className="text-indigo-600 hover:underline" href={existingUrl} target="_blank" rel="noreferrer">{existingUrl.split('/').pop()}</a>
        </p>
      )}
    </div>
  );
};

const ContactCard = ({ contact, index, onChange, onRemove, canRemove }) => {
  return (
    <div className="border border-gray-200 p-4 rounded-lg mb-4 shadow-sm bg-gray-50 relative">
      <h3 className="text-md font-medium text-gray-700 mb-2">
        Contact #{index + 1} {index === 0 && <span className="text-sm text-gray-500">(Primary)</span>}
      </h3>
      <div className="space-y-2">
        <input placeholder="Name" value={contact.name} onChange={(e) => onChange(index, 'name', e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md p-2" />
        <input placeholder="Email" type="email" value={contact.email} onChange={(e) => onChange(index, 'email', e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md p-2" />
        <input placeholder="Mobile (Optional)" value={contact.mobile} onChange={(e) => onChange(index, 'mobile', e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md p-2" />
      </div>
      {canRemove && (
        <button type="button" onClick={() => onRemove(index)} className="absolute top-2 right-2 text-red-500 hover:text-red-700" aria-label={`Remove contact ${index + 1}`}>
          ×
        </button>
      )}
    </div>
  );
};

/* -------------------------
   Main component
   ------------------------- */
const VendorForm = ({ onFormSubmitSuccess }) => {
  const { vendorId } = useParams();
  const navigate = useNavigate();

  const [state, dispatch] = useReducer(reducer, initialState);
  const [currentStep, setCurrentStep] = React.useState(1);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(!!vendorId);
  const [errors, setErrors] = React.useState({});

  const LOCAL_STORAGE_KEY = useMemo(() => `vendor-form-draft-${vendorId || 'new'}`, [vendorId]);

  // --- Load draft if present
  useEffect(() => {
    try {
      const draft = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (draft) {
        const parsed = JSON.parse(draft);
        // don't overwrite file objects from draft (we can't store files), safe fields only
        const safe = {
          ...parsed,
          tradeLicense: null,
          credentials: null,
        };
        dispatch({ type: 'SET_STATE', payload: safe });
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Fetch vendor (edit mode)
  useEffect(() => {
    if (!vendorId) return;
    setIsLoading(true);
    api
      .get(`vendors/${vendorId}/`)
      .then((resp) => {
        const data = resp.data || {};
        const parsedVendorType = parseOtherSpecifyField(data.vendor_type, OTHER_SPECIFY_OPTION);
        const parsedDataCollection = parseOtherSpecifyField(data.data_collection_methods, OTHER_SPECIFY_OPTION);
        const parsedUaeOffices = parseOtherSpecifyField(data.uae_offices, OTHER_SPECIFY_UAE_OFFICE);

        dispatch({
          type: 'SET_STATE',
          payload: {
            vendorName: data.name || '',
            countries: formatArrayForUI(data.countries),
            uaeCities: formatArrayForUI(data.uae_cities),
            uaeOffices: parsedUaeOffices.selected,
            vendorType: parsedVendorType.selected,
            otherVendorType: parsedVendorType.otherValue,
            dataCollectionMethods: parsedDataCollection.selected,
            otherDataCollectionMethod: parsedDataCollection.otherValue,
            expertise: data.expertise || '',
            catiStations: data.cati_stations !== null ? String(data.cati_stations) : '',
            contacts:
              data.contacts && data.contacts.length
                ? data.contacts.map((c) => ({ id: c.id, name: c.name || '', email: c.email || '', mobile: c.mobile || '' }))
                : [{ id: null, name: '', email: '', mobile: '' }],
            fieldTeam: {
              fieldworkers: data.fieldworkers_count !== null ? String(data.fieldworkers_count) : '',
              supervisors: data.supervisors_count !== null ? String(data.supervisors_count) : '',
              managers: data.managers_count !== null ? String(data.managers_count) : '',
            },
            govPermits: data.gov_permits_count !== null ? String(data.gov_permits_count) : '',
            urgentRequests: data.can_manage_urgent_requests ? 'yes' : 'no',
            existingTradeLicenseUrl: data.trade_license_url || '',
            existingCredentialsUrl: data.credentials_document_url || '',
            otherUaeOffice: parsedUaeOffices.otherValue,
          },
        });
        setIsEditing(true);
      })
      .catch((err) => {
        console.error('Error fetching vendor:', err);
        alert(`Error fetching vendor: ${err.response?.data?.detail || err.message || 'Unknown error'}`);
      })
      .finally(() => setIsLoading(false));
  }, [vendorId]);

  // --- Autosave to localStorage on state change (debounced-ish)
  useEffect(() => {
    const payload = { ...state };
    // strip files before saving
    delete payload.tradeLicense;
    delete payload.credentials;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
      } catch {
        // ignore storage errors
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [state, LOCAL_STORAGE_KEY]);

  // --- Validation helpers ---
  const validateStep = useCallback(
    (stepId) => {
      const step = steps.find((s) => s.id === stepId);
      if (!step) return { isValid: true, errors: {} };
      const e = {};
      for (const f of step.fields) {
        switch (f) {
          case 'vendorName':
            if (!state.vendorName || !state.vendorName.trim()) e.vendorName = 'Vendor name is required.';
            break;
          case 'countries':
            if (!state.countries || state.countries.length === 0) e.countries = 'Select at least one country.';
            break;
          case 'uaeCities':
            if (state.countries.includes('UAE') && (!state.uaeCities || state.uaeCities.length === 0)) e.uaeCities = 'Select at least one UAE city.';
            break;
          case 'uaeOffices':
            if (state.countries.includes('UAE') && (!state.uaeOffices || state.uaeOffices.length === 0)) e.uaeOffices = 'Select at least one UAE office.';
            if (state.uaeOffices.includes(OTHER_SPECIFY_UAE_OFFICE) && !state.otherUaeOffice) e.otherUaeOffice = 'Please specify the UAE office.';
            break;
          case 'expertise':
            if (!state.expertise) e.expertise = 'Select expertise.';
            break;
          case 'dataCollectionMethods':
            if (!state.dataCollectionMethods || state.dataCollectionMethods.length === 0) e.dataCollectionMethods = 'Select at least one data collection method.';
            if (state.dataCollectionMethods.includes(OTHER_SPECIFY_OPTION) && !state.otherDataCollectionMethod) e.otherDataCollectionMethod = 'Please specify the other data collection method.';
            break;
          case 'catiStations':
            if (state.dataCollectionMethods.includes('CATI') && (state.catiStations === '' || Number(state.catiStations) < 0)) e.catiStations = 'Enter number of CATI stations.';
            break;
          case 'contacts': {
            const primary = state.contacts[0] || {};
            if (!primary.name || !primary.email) e.contacts = 'Primary contact Name and Email are required.';
            break;
          }
          case 'urgentRequests':
            if (!['yes', 'no'].includes(state.urgentRequests)) e.urgentRequests = 'Please choose yes or no.';
            break;
          default:
            break;
        }
      }
      return { isValid: Object.keys(e).length === 0, errors: e };
    },
    [state],
  );

  const goNext = () => {
    const { isValid, errors: stepErrors } = validateStep(currentStep);
    setErrors((prev) => ({ ...prev, ...stepErrors }));
    if (!isValid) {
      // keep user in same step
      const first = Object.keys(stepErrors)[0];
      alert(stepErrors[first] || 'Please fix errors on this step.');
      return;
    }
    setErrors({});
    setCurrentStep((s) => Math.min(s + 1, steps.length));
  };

  const goPrev = () => {
    setErrors({});
    setCurrentStep((s) => Math.max(1, s - 1));
  };

  // --- Handlers ---
  const setField = (key, value) => dispatch({ type: 'SET', key, value });
  const setContactField = (index, field, value) => dispatch({ type: 'SET_CONTACT', index, field, value });
  const addContact = () => dispatch({ type: 'ADD_CONTACT' });
  const removeContact = (index) => dispatch({ type: 'REMOVE_CONTACT', index });

  const handleFile = (key, file) => dispatch({ type: 'SET', key, value: file });

  // --- submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only allow submission from the final step
    if (currentStep !== steps.length) {
      // If Enter was pressed in an input field, advance to next step instead
      goNext();
      return;
    }

    // validate all steps
    let allErrors = {};
    for (const st of steps) {
      const { errors: stepErrors } = validateStep(st.id);
      allErrors = { ...allErrors, ...stepErrors };
    }
    if (Object.keys(allErrors).length) {
      setErrors(allErrors);
      const firstStepWithError = steps.find((s) => s.fields.some((f) => allErrors[f]));
      if (firstStepWithError) setCurrentStep(firstStepWithError.id);
      alert('Please correct the errors before submitting.');
      return;
    }

    setIsLoading(true);
    try {
      const formData = buildFormDataPayload(state);
      const endpoint = isEditing ? `vendors/${vendorId}/` : 'vendors/';
      const method = isEditing ? 'put' : 'post';
      const resp = await api[method](endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(`Vendor ${isEditing ? 'updated' : 'created'} successfully!`);
      // clear draft after success
      try {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      } catch {}
      if (onFormSubmitSuccess) onFormSubmitSuccess(resp.data);
      navigate('/vendors');
    } catch (err) {
      console.error('Submit error', err);
      const data = err.response?.data || {};
      // build readable message
      const messages = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n');
      alert(`Submission failed: ${err.response?.status || 'Network Error'}\n${messages}`);
      setErrors(typeof data === 'object' ? data : {});
    } finally {
      setIsLoading(false);
    }
  };

  // --- UX: quick helpers used in render ---
  const commonInputClass = 'mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 bg-white';
  const errorTextClass = 'mt-1 text-xs text-red-500';

  if (isLoading && isEditing) return <div className="text-center p-8">Loading vendor data…</div>;

  /* -------------------------
     Render content per step
     ------------------------- */
  const Step1 = (
    <section>
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">Vendor Basic Information</h2>

      <div className="space-y-4">
        <div>
          <Label htmlFor="vendorName" required>Vendor Name</Label>
          <input id="vendorName" value={state.vendorName} onChange={(e) => setField('vendorName', e.target.value)} className={`${commonInputClass} ${errors.vendorName ? 'border-red-500' : ''}`} />
          {errors.vendorName && <p className={errorTextClass}>{errors.vendorName}</p>}
        </div>

        <div>
          <Label htmlFor="countries" required>Countries of Operation</Label>
          <MultiSelect id="countries" options={countryOptions} value={state.countries} onChange={(v) => setField('countries', v)} helper="Hold Ctrl/Cmd to select multiple." />
          {errors.countries && <p className={errorTextClass}>{errors.countries}</p>}
        </div>

        {state.countries.includes('UAE') && (
          <>
            <div>
              <Label htmlFor="uaeCities" required>Cities (UAE)</Label>
              <MultiSelect id="uaeCities" options={uaeCityOptions} value={state.uaeCities} onChange={(v) => setField('uaeCities', v)} />
              {errors.uaeCities && <p className={errorTextClass}>{errors.uaeCities}</p>}
            </div>

            <div>
              <Label htmlFor="uaeOffices" required>Offices (UAE)</Label>
              <MultiSelect id="uaeOffices" options={uaeOfficeOptions} value={state.uaeOffices} onChange={(v) => setField('uaeOffices', v)} />
              {errors.uaeOffices && <p className={errorTextClass}>{errors.uaeOffices}</p>}

              {state.uaeOffices.includes(OTHER_SPECIFY_UAE_OFFICE) && (
                <div className="mt-2">
                  <Label htmlFor="otherUaeOffice" required>Please specify other UAE office</Label>
                  <input id="otherUaeOffice" value={state.otherUaeOffice} onChange={(e) => setField('otherUaeOffice', e.target.value)} className={`${commonInputClass} ${errors.otherUaeOffice ? 'border-red-500' : ''}`} />
                  {errors.otherUaeOffice && <p className={errorTextClass}>{errors.otherUaeOffice}</p>}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );

  const Step2 = (
    <section>
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">Operational Details</h2>
      <div className="space-y-4">
        <div>
          <Label htmlFor="vendorType">Type of Vendor (Optional)</Label>
          <MultiSelect id="vendorType" options={vendorTypeOptions} value={state.vendorType} onChange={(v) => setField('vendorType', v)} helper="Hold Ctrl/Cmd to select multiple." />
          {state.vendorType.includes(OTHER_SPECIFY_OPTION) && (
            <div className="mt-2">
              <Label htmlFor="otherVendorType">Please specify other vendor type</Label>
              <input id="otherVendorType" value={state.otherVendorType} onChange={(e) => setField('otherVendorType', e.target.value)} className={commonInputClass} />
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="expertise" required>Expertise</Label>
          <select id="expertise" value={state.expertise} onChange={(e) => setField('expertise', e.target.value)} className={`${commonInputClass} ${errors.expertise ? 'border-red-500' : ''}`}>
            <option value="">Select Expertise</option>
            <option value="Quantitative">Quantitative</option>
            <option value="Qualitative">Qualitative</option>
            <option value="Both">Both</option>
            <option value="N/A">N/A</option>
          </select>
          {errors.expertise && <p className={errorTextClass}>{errors.expertise}</p>}
        </div>

        <div>
          <Label htmlFor="dataCollectionMethods" required>Data collection methods used</Label>
          <MultiSelect id="dataCollectionMethods" options={dataCollectionOptions} value={state.dataCollectionMethods} onChange={(v) => setField('dataCollectionMethods', v)} />
          {errors.dataCollectionMethods && <p className={errorTextClass}>{errors.dataCollectionMethods}</p>}
          {state.dataCollectionMethods.includes(OTHER_SPECIFY_OPTION) && (
            <div className="mt-2">
              <Label htmlFor="otherDataCollectionMethod">Please specify other</Label>
              <input id="otherDataCollectionMethod" value={state.otherDataCollectionMethod} onChange={(e) => setField('otherDataCollectionMethod', e.target.value)} className={`${commonInputClass} ${errors.otherDataCollectionMethod ? 'border-red-500' : ''}`} />
              {errors.otherDataCollectionMethod && <p className={errorTextClass}>{errors.otherDataCollectionMethod}</p>}
            </div>
          )}
        </div>

        {state.dataCollectionMethods.includes('CATI') && (
          <div>
            <Label htmlFor="catiStations" required>Number of CATI stations</Label>
            <input id="catiStations" type="number" min={0} value={state.catiStations} onChange={(e) => setField('catiStations', e.target.value)} className={`${commonInputClass} ${errors.catiStations ? 'border-red-500' : ''}`} />
            {errors.catiStations && <p className={errorTextClass}>{errors.catiStations}</p>}
          </div>
        )}

        <div>
          <Label>Field team size (Optional)</Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-1">
            <input type="number" placeholder="Fieldworkers" className={commonInputClass} value={state.fieldTeam.fieldworkers} onChange={(e) => setField('fieldTeam', { ...state.fieldTeam, fieldworkers: e.target.value })} min={0} />
            <input type="number" placeholder="Supervisors" className={commonInputClass} value={state.fieldTeam.supervisors} onChange={(e) => setField('fieldTeam', { ...state.fieldTeam, supervisors: e.target.value })} min={0} />
            <input type="number" placeholder="Project Managers" className={commonInputClass} value={state.fieldTeam.managers} onChange={(e) => setField('fieldTeam', { ...state.fieldTeam, managers: e.target.value })} min={0} />
          </div>
        </div>

        <div>
          <Label htmlFor="govPermits">Field team with Gov. permits (Optional)</Label>
          <input id="govPermits" type="number" className={commonInputClass} placeholder="Number of members" value={state.govPermits} onChange={(e) => setField('govPermits', e.target.value)} min={0} />
        </div>
      </div>
    </section>
  );

  const Step3 = (
    <section>
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">Contact Details</h2>
      {state.contacts.map((c, idx) => (
        <ContactCard
          key={c.id ?? `new-${idx}`}
          contact={c}
          index={idx}
          onChange={(i, field, val) => setContactField(i, field, val)}
          onRemove={(i) => removeContact(i)}
          canRemove={idx > 0}
        />
      ))}
      {errors.contacts && <p className={errorTextClass}>{errors.contacts}</p>}
      <button type="button" onClick={addContact} className="py-2 px-4 border rounded-md text-sm font-medium">
        + Add Another Contact
      </button>
    </section>
  );

  const Step4 = (
    <section>
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">Compliance & Documents</h2>
      <div className="space-y-4">
        <div>
          <Label required>Ability to manage urgent requests</Label>
          <div className="flex gap-x-6 mt-2">
            <label className="inline-flex items-center">
              <input type="radio" name="urgent" value="yes" checked={state.urgentRequests === 'yes'} onChange={(e) => setField('urgentRequests', e.target.value)} />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input type="radio" name="urgent" value="no" checked={state.urgentRequests === 'no'} onChange={(e) => setField('urgentRequests', e.target.value)} />
              <span className="ml-2">No</span>
            </label>
          </div>
          {errors.urgentRequests && <p className={errorTextClass}>{errors.urgentRequests}</p>}
        </div>

        <div>
          <Label htmlFor="tradeLicense">Trade license (Optional)</Label>
          <FileInput id="tradeLicense" existingUrl={state.existingTradeLicenseUrl} onFile={(f) => handleFile('tradeLicense', f)} />
        </div>

        <div>
          <Label htmlFor="credentials">Credentials (Optional)</Label>
          <FileInput id="credentials" existingUrl={state.existingCredentialsUrl} onFile={(f) => handleFile('credentials', f)} />
        </div>
      </div>
    </section>
  );

  /* -------------------------
     Main render
     ------------------------- */
  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Stepper */}
        <nav aria-label="Progress" className="mb-8">
          <ol role="list" className="flex space-x-2">
            {steps.map((s) => {
              const active = currentStep === s.id;
              const completed = currentStep > s.id;
              return (
                <li key={s.id} className="flex-1">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(s.id)}
                    className={`w-full py-2 px-3 text-left rounded-md ${active ? 'bg-indigo-600 text-white' : completed ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-600 border'}`}
                    aria-current={active ? 'step' : undefined}
                  >
                    <div className="text-xs font-semibold">{`Step ${s.id}`}</div>
                    <div className="text-sm">{s.name}</div>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <form
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            // Prevent Enter key from submitting the form unless on the final step
            if (e.key === 'Enter' && currentStep !== steps.length && e.target.tagName !== 'TEXTAREA') {
              e.preventDefault();
            }
          }}
          className="bg-white shadow-xl rounded-lg p-6 sm:p-8 space-y-6"
          noValidate
        >
          {currentStep === 1 && Step1}
          {currentStep === 2 && Step2}
          {currentStep === 3 && Step3}
          {currentStep === 4 && Step4}

          <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
            <div>
              <button type="button" disabled={currentStep === 1 || isLoading} onClick={goPrev} className="py-2 px-4 border rounded-md text-sm font-medium disabled:opacity-50">
                Previous
              </button>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-500 mr-4">Step {currentStep} of {steps.length}</div>
              {currentStep < steps.length ? (
                <button type="button" onClick={goNext} disabled={isLoading} className="py-2 px-4 rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                  Next
                </button>
              ) : (
                <button type="submit" disabled={isLoading} className="py-2 px-4 rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50">
                  {isLoading ? 'Submitting...' : isEditing ? 'Update Vendor' : 'Create Vendor'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
  
export default VendorForm;
